import { useEffect, useState } from "react";
import { Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@racoon/ui";
import { Namespace, useTypedT } from "@racoon/i18n";
import { ShiftFreeSlot, formatSlotLabel, normalizeTimeInput } from "../../model/shift-free-slots";

interface ShiftSlotBarProps {
  slots: ShiftFreeSlot[];
  /** Index into `slots`, or null while none is chosen. */
  selectedSlotIndex: number | null;
  onSlotSelect: (index: number) => void;
  /** "HH:mm" bounds of the route being recorded, narrowed inside the slot. */
  window: { start: string; end: string };
  onWindowChange: (window: { start: string; end: string }) => void;
  /** Bounds that fall outside the chosen slot, flagged rather than corrected. */
  invalid: { start: boolean; end: boolean };
  isLoading?: boolean;
}

interface TimeFieldProps {
  id: string;
  label: string;
  value: string;
  disabled?: boolean;
  invalid?: boolean;
  onCommit: (value: string) => void;
}

/**
 * A plain text field rather than `type="time"`: the native widget draws a clock
 * that cannot be reliably styled away, and its min/max flag a bad value in a
 * shape we cannot restyle either. Validity is the caller's to decide.
 */
const TimeField = ({ id, label, value, disabled, invalid, onCommit }: TimeFieldProps) => {
  const [draft, setDraft] = useState(value);

  // A change made outside this field — picking another slot — has to show up here.
  useEffect(() => setDraft(value), [value]);

  const commit = () => onCommit(draft);

  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="font-medium">{label}</span>
      <Input
        id={id}
        type="text"
        size="sm"
        radius="lg"
        inputMode="numeric"
        maxLength={5}
        className="!w-[68px] bg-white text-center tabular-nums"
        value={draft}
        disabled={disabled}
        // The kit paints aria-invalid with the destructive border.
        aria-invalid={invalid}
        onChange={(event) => setDraft(normalizeTimeInput(event.target.value))}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur();
        }}
      />
    </label>
  );
};

/**
 * Picks the stretch of the shift the recorded route goes into: a free slot
 * between the shift's existing routes, then its exact bounds inside that slot.
 */
export const ShiftSlotBar = ({
  slots,
  selectedSlotIndex,
  onSlotSelect,
  window,
  onWindowChange,
  invalid,
  isLoading,
}: ShiftSlotBarProps) => {
  const t = useTypedT(Namespace.Routes);
  const selectedSlot = selectedSlotIndex == null ? undefined : slots[selectedSlotIndex];

  const placeholder = isLoading
    ? t("loading")
    : slots.length === 0
      ? t("slotBarNoSlots")
      : t("slotBarPlaceholder");

  const commitBound = (bound: "start" | "end") => (raw: string) =>
    onWindowChange({ ...window, [bound]: raw });

  return (
    // Every item keeps its width, so the row wraps instead of overflowing
    // when the sidebar and the map leave it too little room.
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl bg-slate-50 px-4 py-2.5">
      <span className="shrink-0 text-sm font-medium text-brand-blue">{t("slotBarLabel")}</span>

      <Select
        value={selectedSlotIndex == null ? "" : String(selectedSlotIndex)}
        onValueChange={(value) => onSlotSelect(Number(value))}
        disabled={slots.length === 0}
      >
        {/* The UI kit's cn() only joins, so the base w-full/h-10 needs overriding. */}
        <SelectTrigger
          id="pseudo-route-slot-select"
          className="!h-8 !w-[184px] shrink-0 rounded-lg bg-white"
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {slots.map((slot, index) => (
            <SelectItem key={slot.start.toISOString()} value={String(index)}>
              {formatSlotLabel(slot, t("slotBarNextDay"))}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="ml-auto flex shrink-0 items-center gap-4">
        <TimeField
          id="pseudo-route-slot-start"
          label={t("slotBarStart")}
          value={window.start}
          disabled={!selectedSlot}
          invalid={invalid.start}
          onCommit={commitBound("start")}
        />
        <TimeField
          id="pseudo-route-slot-end"
          label={t("slotBarEnd")}
          value={window.end}
          disabled={!selectedSlot}
          invalid={invalid.end}
          onCommit={commitBound("end")}
        />
      </div>
    </div>
  );
};
